import {
    AccountInfo,
    AuthenticationResult,
    AuthError,
    createNestablePublicClientApplication,
    InteractionRequiredAuthError, IPublicClientApplication,
} from "@azure/msal-browser";


const SPA_CLIENT_ID = localStorage.getItem("owa_clientId") ?? "";

const API_APP_ID_URI = `api://${globalThis.location.host}/${SPA_CLIENT_ID}`;
export const API_ACCESS_AS_USER_SCOPE = `${API_APP_ID_URI}/access_as_user`; // should be created same in azure portal and in manifest

/** MSAL singleton (create once, iframe-safe) */
let pcaPromise: Promise<IPublicClientApplication> | null = null;
export function getPca(): Promise<IPublicClientApplication> {
    pcaPromise ??= createNestablePublicClientApplication({
        auth: {
            clientId: SPA_CLIENT_ID,
            authority: "https://login.microsoftonline.com/common",
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: "sessionStorage",
            storeAuthStateInCookie: false,
        }
    });
    return pcaPromise;
}

/** Ensure we have an active account; if none, do interactive login (popup). */
async function getActiveAccountOrLogin(
    scopes: string[]
): Promise<{ account: AccountInfo; result?: AuthenticationResult }> {
    const pca = await getPca();
    const existing = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
    if (existing) {
        pca.setActiveAccount(existing);
        return { account: existing };
    }
    // No account: interactive login (popup only; redirect breaks inside Outlook taskpane)
    const loginResult = await pca.loginPopup({ scopes, prompt: "select_account" });
    if (!loginResult.account) throw new Error("Login produced no account.");
    pca.setActiveAccount(loginResult.account);
    return { account: loginResult.account, result: loginResult };
}

/** Acquire token (cache-first → silent → guarded popup). */
export async function getMsalAccessToken(scopes: string[]): Promise<string> {
    const pca = await getPca();
    const { account } = await getActiveAccountOrLogin(scopes);

    try {
        const silent = await pca.acquireTokenSilent({ scopes, account });
        return silent.accessToken;
    } catch (error_: unknown) {
        // In nested environments, only popup is safe
        if (
            error_ instanceof InteractionRequiredAuthError ||
            String((error_ as AuthError)?.errorCode || "").includes("interaction_required")
        ) {
            const popup = await pca.acquireTokenPopup({ scopes });
            return popup.accessToken;
        }
        // Some hosts throw "unsupported_method" if an interactive path was attempted incorrectly
        if (String((error_ as AuthError)?.errorCode || "").includes("unsupported_method")) {
            const popup = await pca.acquireTokenPopup({ scopes });
            return popup.accessToken;
        }
        // Last chance
        const popup = await pca.acquireTokenPopup({ scopes });
        return popup.accessToken;
    }
}
/** Office SSO (best UX): returns a Graph token if available. */
export async function getOfficeToken(): Promise<string> {
    try {
        const ssoToken = await OfficeRuntime.auth.getAccessToken({
            allowSignInPrompt: true,
            allowConsentPrompt: true,
            forMSGraphAccess: true, // Request token for Graph
        });
        return ssoToken;
    } catch (err: unknown) {
        // Common codes: 13001 (no user), 13002 (consent needed), 13006 (add-in mismatch), 13012 (policy)
        // Silently fail and let fallback authentication methods handle it
        const error = err as { code?: string; message?: string };
        throw new Error(`Office SSO failed: ${error.code ?? "unknown"} - ${error.message ?? "unknown"}`);
    }
}


export async function getTokenUnified(): Promise<string> {
    // 1) Prefer Office SSO (Graph token)
    try {
        const officeToken = await getOfficeToken();
        return officeToken;
    } catch {
        // continue to fallback
    }

    // 2) MSAL fallback

    return await getMsalAccessToken([API_ACCESS_AS_USER_SCOPE]);
}
